import {
  RocketOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Tag } from 'antd';
import moment from 'moment';
import { ReactNode } from 'react';

import { ReleaseNote, ReleaseNoteItem } from '../../../types';
import { ReleaseNoteContainer, SectionHeader, SectionItems } from './styles';

type SectionProps = {
  title: string;
  icon: ReactNode;
  accent: string;
  soft: string;
  supColor?: string;
  items: ReleaseNoteItem[];
  showDeadline?: boolean;
};

function Section({
  title,
  icon,
  accent,
  soft,
  supColor,
  items,
  showDeadline,
}: SectionProps) {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <div>
      <SectionHeader accent={accent} soft={soft}>
        <span className="section-icon">{icon}</span>
        <span className="section-title">{title}</span>
      </SectionHeader>
      <SectionItems accent={accent} soft={soft}>
        {items.map((item, index) => (
          <div className="release-item" key={`${title}-${index}`}>
            <span className="release-item-title">{item.title}</span>
            {item.description && (
              <span className="release-item-description">
                {item.description}
              </span>
            )}
            {(item.sup || (showDeadline && item.deadline)) && (
              <div className="release-item-meta">
                {item.sup && (
                  <Tag color={supColor} style={{ margin: 0 }}>
                    {item.sup}
                  </Tag>
                )}
                {showDeadline && item.deadline && (
                  <span className="release-deadline">
                    Prazo: {moment(item.deadline).format('DD/MM/YYYY')}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </SectionItems>
    </div>
  );
}

type ReleaseNoteViewProps = {
  releaseNote: ReleaseNote;
};

export function ReleaseNoteView({ releaseNote }: ReleaseNoteViewProps) {
  const { content } = releaseNote;
  const isEmpty =
    !content ||
    ((content.features?.length || 0) === 0 &&
      (content.fixes?.length || 0) === 0 &&
      (content.warnings?.length || 0) === 0);

  return (
    <ReleaseNoteContainer>
      <span className="release-date">
        Liberada em {moment(releaseNote.release_date).format('DD/MM/YYYY')}
      </span>
      {isEmpty ? (
        <span className="release-empty">Sem itens nesta versão.</span>
      ) : (
        <>
          <Section
            title="Novidades"
            icon={<RocketOutlined />}
            accent="#1890ff"
            soft="#e6f7ff"
            supColor="blue"
            items={content.features}
          />
          <Section
            title="Correções"
            icon={<ToolOutlined />}
            accent="#fa8c16"
            soft="#fff7e6"
            supColor="orange"
            items={content.fixes}
          />
          <Section
            title="Atenção"
            icon={<WarningOutlined />}
            accent="#f5222d"
            soft="#fff1f0"
            supColor="red"
            items={content.warnings}
            showDeadline
          />
        </>
      )}
    </ReleaseNoteContainer>
  );
}
